/**
 * About.tsx
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Personal but professional tone — no name, no photo, no age
 * - Brașov native, economics + IT + entrepreneurship background
 * - Generous whitespace, serif headings, muted earth tones
 */

import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { ArrowRight, MapPin, GraduationCap, Briefcase, Code2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteFooter from "@/components/SiteFooter";
import SiteHeader from "@/components/SiteHeader";

const STREET_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/about-brasov-street-cysdiJLeAYPRL6o2Qz7ZBB.webp";

export default function About() {
  return (
    <>
    <Helmet>
      <title>About Brașov Gateway — Local Experts for Foreign Movers</title>
      <meta name="description" content="Local experts helping foreigners relocate to Brașov, Romania. Real estate advisory, company setup, and relocation support — done right the first time." />
      <meta property="og:title" content="About Brașov Gateway — Local Experts for Foreign Movers" />
      <meta property="og:description" content="Local experts helping foreigners relocate to Brașov, Romania. Real estate advisory, company setup, and relocation support — done right the first time." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/home-hero-brasov-v2_fd2b2f8b.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/about" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="About Brașov Gateway — Local Experts for Foreign Movers" />
      <link rel="canonical" href="https://brasovgateway.com/about" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": "https://brasovgateway.com/about",
          "name": "About Brașov Gateway",
          "url": "https://brasovgateway.com/about",
          "description": "Brașov Gateway helps foreigners move to, invest in, and build a life in Brașov, Romania — real estate, company setup, and relocation support.",
          "publisher": {
            "@type": "Organization",
            "name": "Brașov Gateway",
            "url": "https://brasovgateway.com"
          }
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${STREET_IMG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        </div>

        <div className="container relative z-10 pb-16 text-white">
          <p className="text-sm uppercase tracking-[0.2em] mb-4 text-white/70">About</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif max-w-3xl leading-tight">
            Born here.<br />Still here.<br />For a reason.
          </h1>
        </div>
      </section>

      {/* Main Introduction */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <p className="text-2xl md:text-3xl font-serif leading-relaxed text-foreground mb-8">
              I grew up in Brașov. I've watched it change — slowly at first, then all at once. I know which streets flood in spring, which neighbourhoods are genuinely up-and-coming, and which ones are just marketed that way.
            </p>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                That kind of knowledge doesn't come from a relocation guide. It comes from living here — running businesses here, dealing with Romanian bureaucracy here, building a life here.
              </p>
              <p>
                Brașov Gateway exists because I kept getting the same questions from people who were seriously considering a move or an investment: <em>"Who can I actually trust to tell me how things work?"</em> This is my answer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center">
              Where I come from
            </h2>

            <div className="grid md:grid-cols-2 gap-12">
              {[
                {
                  icon: GraduationCap,
                  title: "Economics",
                  description: "I studied economics — which means I understand how businesses are structured, how money moves, and how to read a market. It also means I have a healthy scepticism for projections that look too good on paper."
                },
                {
                  icon: Code2,
                  title: "Technology & IT",
                  description: "I've worked in IT and built technology-driven businesses. I understand how modern companies operate, what infrastructure looks like in practice, and why Brașov's growing tech ecosystem is a genuine asset — not just a talking point."
                },
                {
                  icon: Briefcase,
                  title: "Entrepreneurship",
                  description: "I've started and run businesses in Romania. I've dealt with ANAF, ONRC, Romanian banks, and local administrations — not as a consultant advising from a distance, but as someone who had to make it work."
                },
                {
                  icon: MapPin,
                  title: "Local Administration",
                  description: "I've been involved in public administration, which gave me a clear picture of how decisions get made at the local level — and how to navigate the system without losing time or money to avoidable friction."
                }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-3 text-foreground">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif mb-10">
              Why this matters to you
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                When you're making a decision about where to live, where to invest, or where to build a business, the quality of your local knowledge is the difference between a good outcome and an expensive mistake.
              </p>
              <p>
                Most relocation services give you a checklist. I give you context. There's a difference between knowing that a neighbourhood is "central" and knowing that parking is impossible, that the building management is dysfunctional, and that the apartment three floors up has been for sale for two years for a reason.
              </p>
              <p>
                I work with a small number of clients at a time — deliberately. I'm not running a volume business. If I take on your case, it means I believe I can genuinely help, and I'll give it the attention it deserves.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages */}
      <section className="py-16 bg-muted/30">
        <div className="container">
          <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-16">
            <div className="flex-shrink-0">
              <MessageSquare className="w-12 h-12 text-primary" />
            </div>
            <div>
              <h3 className="text-2xl font-serif mb-3 text-foreground">Languages</h3>
              <p className="text-muted-foreground leading-relaxed text-lg">
                I work in <strong className="text-foreground">Romanian</strong>, <strong className="text-foreground">English</strong>, and <strong className="text-foreground">Arabic</strong>. All communication, documentation review, and advisory sessions can be conducted in any of these languages — whichever you're more comfortable with.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              Let's talk about what you need
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              No commitment, no sales pitch. Just a conversation about what you're trying to do and whether I'm the right person to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link href="/contact">
                <Button size="lg" className="group">
                  Get in Touch
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a href="/#services">
                <Button size="lg" variant="outline">
                  Explore Services
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
